import { useCallback, type MouseEvent as ReactMouseEvent } from "react";

import { V2CopyButton } from "@stacklet/ui";

import { StatusIcon } from "app/components/icons/StatusIcon";

import { TooltipLink } from "./TooltipLink";

interface CopyButtonWrapperProps {
  enabled: boolean;
  value: string;
  children: React.ReactNode;
  copyLabel?: string;
  truncate?: boolean;
}

const CopyButtonWrapper = ({
  enabled,
  value,
  children,
  copyLabel,
  truncate,
}: CopyButtonWrapperProps) => {
  if (enabled) {
    return (
      <V2CopyButton
        aria-label={`Copy ${copyLabel ?? "value"}`}
        className="w-full min-w-0 items-center justify-between"
        copyLabel={copyLabel}
        tooltipTarget="icon"
        value={value}
      >
        {children}
      </V2CopyButton>
    );
  }
  if (truncate) {
    return <div className="w-full min-w-0">{children}</div>;
  }
  return children;
};

interface Props {
  name: string;
  to: string;
  description?: string | null;
  errorMessage?: string;
  copyButton?: boolean;
  copyValue?: string;
  copyLabel?: string;
  truncate?: boolean;
}

export function TableLink({
  copyButton = false,
  description,
  errorMessage,
  name,
  to,
  copyValue,
  copyLabel,
  truncate = false,
}: Props) {
  const tooltipDescription = description || (truncate ? name : "");

  const handleTableLinkClick = useCallback((e: ReactMouseEvent<Element>) => {
    e.stopPropagation();
  }, []);

  return (
    <CopyButtonWrapper
      copyLabel={copyLabel}
      enabled={copyButton}
      truncate={truncate}
      value={copyValue || name}
    >
      <span
        className={
          truncate
            ? "flex w-full min-w-0 items-center gap-xxs"
            : "inline-flex min-w-0 max-w-full flex-1 items-center gap-xxs"
        }
      >
        {errorMessage ? (
          <span className="leading-none inline-flex shrink-0 items-center [&_svg]:block">
            <StatusIcon message={errorMessage} status="error" />
          </span>
        ) : null}
        <span
          className="min-w-0 max-w-full flex-1 overflow-hidden"
          onClick={handleTableLinkClick}
        >
          <TooltipLink
            description={tooltipDescription}
            to={to}
            truncate={truncate}
          >
            {name}
          </TooltipLink>
        </span>
      </span>
    </CopyButtonWrapper>
  );
}
